import * as R from 'ramda';
import { handleActions } from 'redux-actions';

import { getUpdatedIds } from './helpers';
import * as commonReducers from './common';
import * as actions from '../actions/tasks';
import { TABS_STATUSES } from '../../api/constants';

const {
  create, update, fetch, remove,
} = commonReducers.operationTypes;

const createTaskSuccessReducer = (state, { payload }) => {
  const myId = localStorage.getItem('userId');
  const isMyTask = payload.assignedTo === myId;

  const getTotalCount = () => {
    if (state.activeTab === TABS_STATUSES.UNDONE) {
      // own task
      if (isMyTask && R.isNil(state.filters.assignee[0])) {
        return state.count + 1;
      }

      // unassigned
      if (
        !R.isNil(state.filters.assignee[0]) &&
        state.filters.assignee.includes('UNASSIGNED') && !payload.assignedTo
      ) {
        return state.count + 1;
      }

      // assigned
      if (
        !R.isNil(state.filters.assignee[0]) &&
        state.filters.assignee.includes(payload.assignedTo)
      ) {
        return state.count + 1;
      }
    }

    return state.count;
  };

  return {
    ...state,
    ids: state.ids.concat(payload._id),
    [create]: false,
    items: {
      ...state.items,
      [payload._id]: payload,
    },
    count: getTotalCount(),
  };
};

const deleteTaskSuccessReducer = (state, { payload }) => ({
  ...state,
  [remove]: false,
  count: state.count - 1,
  ids: R.without([payload._id], state.ids),
  items: R.dissoc(payload._id, state.items),
});

export const updateTasksPrioritySuccessReducer = (state, { idsList = [] }) => {
  const normalizedObject = {};
  const normalizedTasks = idsList.reduce((prevData, id, index) => {
    normalizedObject[id] = {
      ...state.items[id],
      priority: index,
    };
    return normalizedObject;
  }, normalizedObject);

  return {
    ...state,
    [update]: false,
    items: {
      ...state.items,
      ...normalizedTasks,
    },
  };
};

const updateTaskSuccessReducer = (state, { payload }) => {
  if (payload.isMultipleTasks) {
    return updateTasksPrioritySuccessReducer(state, payload);
  }

  const getTotalCount = () => {
    const prevTaskData = R.path(['items', payload._id], state);

    if (state.activeTab === TABS_STATUSES.UNDONE) {
      // if task's status was changed
      if (!prevTaskData.status && payload.status) {
        return state.count - 1;
      }
      if (prevTaskData.status && !payload.status) {
        return state.count + 1;
      }

      // if assignee was changed
      if (
        payload.assignedTo && payload.assignedTo !== prevTaskData.assignedTo &&
        R.isNil(state.filters.assignee[0])
      ) {
        return state.count - 1;
      }
    }

    if (state.activeTab === TABS_STATUSES.DONE) {
      // if task's status was changed
      if (prevTaskData.status && !payload.status) {
        return state.count - 1;
      }
      if (!prevTaskData.status && payload.status) {
        return state.count + 1;
      }
    }

    // if assignee was changed and doesn't match to filter
    if (
      payload.assignedTo !== prevTaskData.assignedTo &&
      !R.isNil(state.filters.assignee[0]) &&
      !state.filters.assignee.includes(payload.assignedTo)
    ) {
      return state.count - 1;
    }

    return state.count;
  };

  return {
    ...state,
    [update]: payload.continueUpdating || false,
    items: {
      ...state.items,
      [payload._id]: {
        ...state.items[payload._id],
        ...payload,
      },
    },
    count: getTotalCount(),
  };
};

const fetchTasksSuccessReducer = (
  state,
  {
    payload: {
      tasks = {},
      ids,
      existsMore,
      notNewPage = false,
      count = 0,
      polling = false,
      overview = false,
    } = {},
  },
) => {
  const newTasks = Object.keys(tasks).reduce((acc, key) => {
    let newTask;

    if (overview) {
      newTask = {
        ...R.path(['items', key], state),
        ...tasks[key],
      };
    } else {
      const prevData = R.pick(
        ['request', 'requestId'],
        R.pathOr({}, ['items', key], state),
      );

      newTask = { ...prevData, ...tasks[key] };
    }

    return R.assoc(key, newTask, acc);
  }, {});

  return {
    ...state,
    count,
    [fetch]: false,
    page: notNewPage ? state.page : state.page + 1,
    existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
    ids: getUpdatedIds(state, ids, polling),
    items: { ...state.items, ...newTasks },
  };
};

const setFilterReducer = (state, { payload: { filterType, data } }) => ({
  ...state,
  ...(((R.equals(filterType, 'dateRange') &&
    !R.equals(state.filters.dateRange, data)) ||
    (R.equals(filterType, 'assignee') &&
      !R.equals(state.filters.assignee, data))) && {
    ids: [],
    page: 0,
    existsMore: false,
  }),
  filters: {
    ...state.filters,
    [filterType]: data,
  },
});

const setActiveTabReducer = (state, { payload }) => ({
  ...state,
  count: 0,
  activeTab: payload,
});

const resetFiltersReducer = state => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  filters: {
    dateRange: [],
    assignee: [],
  },
});

const resetDataReducer = state => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  existsMore: false,
  ...commonReducers.defaultState,
});

export const tasksReducer = handleActions(
  {
    // create
    [actions.createTaskRequest]: commonReducers.commonRequestReducer(create),
    [actions.createTaskFailure]: commonReducers.commonFailureReducer(create),
    [actions.createTaskSuccess]: createTaskSuccessReducer,

    // update
    [actions.updateTaskRequest]: commonReducers.commonRequestReducer(update),
    [actions.updateTaskFailure]: commonReducers.commonFailureReducer(update),
    [actions.updateTaskSuccess]: updateTaskSuccessReducer,

    // delete
    [actions.deleteTaskRequest]: commonReducers.commonRequestReducer(remove),
    [actions.deleteTaskFailure]: commonReducers.commonFailureReducer(remove),
    [actions.deleteTaskSuccess]: deleteTaskSuccessReducer,

    // fetch
    [actions.fetchTasksRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.fetchTasksFailure]: commonReducers.commonFailureReducer(fetch),
    [actions.fetchTasksSuccess]: fetchTasksSuccessReducer,

    [actions.setPage]: commonReducers.commonSetPageReducer,

    [actions.resetTasksData]: resetDataReducer,

    [actions.setActiveTab]: setActiveTabReducer,

    [actions.setFilter]: setFilterReducer,
    [actions.resetFilters]: resetFiltersReducer,
  },
  {
    ids: [],
    page: 0,
    count: 0,
    existsMore: true,
    filters: {
      dateRange: [],
      assignee: [],
    },
    activeTab: TABS_STATUSES.UNDONE,
    ...commonReducers.defaultState,
  },
);



// WEBPACK FOOTER //
// ./src/redux/reducers/tasks.js